import React, { useEffect, useMemo, useState } from 'react';
import {
    Drawer, Box, Typography, Button, Checkbox, Chip, Divider, TextField, InputAdornment, CircularProgress,
} from '@mui/material';
import SearchOutlinedIcon from '@material-ui/icons/SearchOutlined';
import { listClientsService, listOrgClassesService, getSectionLinkedModulesService } from '../../../services/client.service';
import {
    getLinkedLearningProgramService, linkLearningProgramService, linkSectionLearningProgramService,
} from '../../../services/learning_programs.service';
import { TOAST_TYPES } from '../../../utils/constants';
import { toast } from '../../../utils/utils';

/**
 * The assignment drawer on My Programs (Network deck slide 62, Partner deck
 * slide 63): pick where a published program goes, without leaving the program.
 *
 * One drawer, two targets — always the caller's NEXT level down:
 *
 *   target 'schools'  partner -> its networks and standalone schools
 *                     network -> its own schools
 *                     (the list is scoped server-side by /dqin/client/list; a
 *                     school inside a network gets the program from its network,
 *                     never straight from the partner)
 *   target 'classes'  school  -> its own classes
 *                     (/dqin/client/org/sections/list scopes a school inside a
 *                     network to its own classes)
 *
 * Each pick is the same link My Schools -> Link programs (schools) or Manage
 * Classes -> Link Programs (classes) makes, one call per row. Rows that already
 * have the program are shown ticked and locked — there is no unlink here, so
 * the drawer only adds. Linking costs nothing and needs nothing (R88).
 */

const PAGE_LIMIT = 100; // the client list endpoint's maximum page size

/** Every row of the caller's school list, across pages. */
const loadAllSchools = async () => {
    const rows = [];
    for (let page = 1; page <= 20; page += 1) {
        // eslint-disable-next-line no-await-in-loop
        const res = await listClientsService({ page, limit: PAGE_LIMIT });
        const batch = res?.data?.clients || [];
        rows.push(...batch);
        const pages = Number(res?.data?.pages) || 1;
        if (page >= pages || batch.length === 0) break;
    }
    return rows;
};

const isPublishedClass = (c) => Number(c.lifecycle_status) === 5 || c.lifecycle_status === undefined || c.lifecycle_status === null;

/**
 * Everything that differs between the two targets. `status` is per row:
 * 'assigned' (has it), 'whole_school' (shared with every class already), or
 * null (can be picked).
 */
const TARGETS = {
    schools: {
        title: 'Assign to schools',
        noun: (n) => (n === 1 ? 'school' : 'schools'),
        searchPlaceholder: 'Search schools',
        empty: 'No schools to assign to yet.',
        key: (r) => `${r.org_code}::${r.institute_code || ''}`,
        name: (r) => r.org_name,
        detail: (r) => (r.is_school_network
            ? `Network${Array.isArray(r.schools) ? ` · ${r.schools.length} school${r.schools.length === 1 ? '' : 's'}` : ''}`
            : 'School'),
        intro: (rows) => (rows.some((r) => !r.institute_code)
            ? 'Choose the networks and schools that get this program. A network makes it available to its own schools.'
            : 'Choose the schools that get this program. Each school then links it to its classes.'),
        load: async ({ authUser, program }) => {
            const all = await loadAllSchools();
            // A direct workspace's list holds its own organisation: not a school to assign to.
            const rows = all.filter((r) => !(r.org_code === authUser?.org_code && !r.institute_code));
            const status = await Promise.all(rows.map((r) =>
                getLinkedLearningProgramService(r.org_code, r.institute_code || null)
                    .then((res) => ((res?.data?.linked_learning_programs || []).some((p) => p.code === program.code) ? 'assigned' : null))
                    .catch(() => null)));
            return { rows, status };
        },
        assign: (r, program) => linkLearningProgramService(r.org_code, [program.code], r.institute_code || null),
    },
    classes: {
        title: 'Assign to classes',
        noun: (n) => (n === 1 ? 'class' : 'classes'),
        searchPlaceholder: 'Search classes',
        empty: 'No classes yet. Add a class in My Classes first.',
        key: (r) => String(r.section_code),
        name: (r) => r.section_name || r.section_code,
        detail: (r) => [r.section_level, isPublishedClass(r) ? null : 'Draft'].filter(Boolean).join(' · ') || 'Class',
        intro: () => 'Choose the classes that get this program. Learners see it once their class is published.',
        load: async ({ authUser, program }) => {
            const rows = await listOrgClassesService(authUser?.org_code);
            const status = await Promise.all(rows.map((r) =>
                getSectionLinkedModulesService(authUser?.org_code, r.section_code)
                    .then((res) => {
                        const d = res?.data || {};
                        const has = (list) => (list || []).some((m) => m.module_code === program.code);
                        if (has(d.org_wide)) return 'whole_school';
                        return has(d.section_exclusive) || has(d.multi_section) ? 'assigned' : null;
                    })
                    .catch(() => null)));
            return { rows, status };
        },
        assign: (r, program, authUser) => linkSectionLearningProgramService(authUser?.org_code, r.section_code, [program.code]),
    },
};

const AssignProgramToSchoolsDrawer = ({ open, program, authUser, target = 'schools', onClose, onAssigned }) => {
    const T = TARGETS[target] || TARGETS.schools;
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [rows, setRows] = useState([]);
    const [statusByKey, setStatusByKey] = useState({});
    const [selected, setSelected] = useState(new Set());
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (!open || !program?.code) return undefined;
        let cancelled = false;
        setSelected(new Set());
        setSearch('');
        setLoading(true);
        (async () => {
            try {
                const { rows: loaded, status } = await T.load({ authUser, program });
                if (cancelled) return;
                setRows(loaded);
                setStatusByKey(Object.fromEntries(loaded.map((r, i) => [T.key(r), status[i]])));
            } catch (err) {
                if (!cancelled) {
                    setRows([]);
                    toast(TOAST_TYPES.DANGER, T.title, `Could not load your ${T.noun(2)}.`);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, program?.code, target]); // eslint-disable-line react-hooks/exhaustive-deps

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q ? rows.filter((r) => String(T.name(r) || '').toLowerCase().includes(q)) : rows;
    }, [rows, search, T]);

    const toggle = (r) => {
        const k = T.key(r);
        if (statusByKey[k]) return;
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(k)) next.delete(k); else next.add(k);
            return next;
        });
    };

    const handleAssign = async () => {
        const targets = rows.filter((r) => selected.has(T.key(r)));
        if (!targets.length) return;
        setSaving(true);
        const done = [];
        const failed = [];
        for (const t of targets) {
            try {
                // eslint-disable-next-line no-await-in-loop
                const res = await T.assign(t, program, authUser);
                if (res?.data?.success) done.push(t); else failed.push(t);
            } catch (e) {
                failed.push(t);
            }
        }
        setSaving(false);
        if (done.length) {
            setStatusByKey((prev) => ({ ...prev, ...Object.fromEntries(done.map((d) => [T.key(d), 'assigned'])) }));
            setSelected(new Set());
            toast(TOAST_TYPES.SUCCESS, 'Assigned',
                `${program.full_name || 'The program'} is now available to ${done.length === 1 ? T.name(done[0]) : `${done.length} ${T.noun(done.length)}`}.`);
            if (onAssigned) onAssigned(program, done);
        }
        if (failed.length) {
            // The backend's reason has already been shown by invokeApi.
            toast(TOAST_TYPES.WARN, T.title, `Could not assign to ${failed.map(T.name).join(', ')}.`);
        }
        if (!failed.length) onClose();
    };

    return (
        <Drawer anchor="right" open={open} onClose={saving ? undefined : onClose}>
            <Box sx={{ width: { xs: '100vw', sm: 440 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ p: 3, pb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{T.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {program?.full_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        {T.intro(rows)}
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={T.searchPlaceholder}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ mt: 2 }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlinedIcon fontSize="small" /></InputAdornment> }}
                    />
                </Box>
                <Divider />
                <Box sx={{ flex: 1, overflowY: 'auto', px: 1 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
                    ) : visible.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                            {rows.length === 0 ? T.empty : `No ${T.noun(2)} match your search.`}
                        </Typography>
                    ) : visible.map((r) => {
                        const k = T.key(r);
                        const status = statusByKey[k];
                        return (
                            <Box
                                key={k}
                                onClick={() => toggle(r)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1, borderRadius: 1,
                                    cursor: status ? 'default' : 'pointer',
                                    '&:hover': { bgcolor: status ? 'transparent' : 'action.hover' },
                                }}
                            >
                                <Checkbox checked={!!status || selected.has(k)} disabled={!!status || saving} size="small" />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{T.name(r)}</Typography>
                                    <Typography variant="caption" color="text.secondary">{T.detail(r)}</Typography>
                                </Box>
                                {status && (
                                    <Chip
                                        label={status === 'whole_school' ? 'Whole school' : 'Assigned'}
                                        size="small" color="success" variant="outlined"
                                    />
                                )}
                            </Box>
                        );
                    })}
                </Box>
                <Divider />
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        {selected.size ? `${selected.size} selected` : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={onClose} disabled={saving}>Cancel</Button>
                        <Button variant="contained" onClick={handleAssign} disabled={!selected.size || saving || loading}>
                            {saving ? 'Assigning…' : 'Assign'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Drawer>
    );
};

export default AssignProgramToSchoolsDrawer;
